import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useStateMachine } from 'little-state-machine';
import {
  Button, Container, Form, Row, Col, OverlayTrigger, Tooltip,
} from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router-dom';
import { CustomPricingPropType, useCustomPricing } from '../../../hooks/useCustomPricing';
import updateLocationCreateAction from '../../../actions/updateLocationCreateAction';
import useOrganization from '../../../hooks/useOrganization';
import { LOC_CREATE_STAGES } from '../locationCreation/LocationCreation';

import './LocationPricingCalculator.scss';
import './InfoButton.scss';

import { PLANS, PLAN_TYPES } from '../../../common/constants';

/**
 * Pricing doc: https://docs.google.com/document/d/1jXPoV6FTvm0eMbeo9PaM1GuwpTQd81nbVAHDuI-zYqU/edit
 */
function LocationPricingCalculator({ givenPricing, hideButtons }) {
  const { actions, state } = useStateMachine({ updateLocationCreateAction });
  const defaultValues = state.locationCreateData;

  const { isBusiness } = useOrganization();
  // prevent direct navigation to this page or recipient access
  const [goBack, setGoBack] = useState(!(defaultValues.plan && isBusiness));

  const {
    setValue,
    watch,
    getValues,
  } = useForm({ mode: 'all', defaultValues });

  const {
    num_monthly_pickups: monthlyPickups,
    num_additional_floors: addlFloors,
    num_additional_buildings: addlBuildings,
  } = watch();

  const [prevPickups, setPrevPickups] = useState(monthlyPickups);
  const [prevFloors, setPrevFloors] = useState(addlFloors);
  const [prevBuildings, setPrevBuildings] = useState(addlBuildings);

  // don't use null data if user clears an input. use previously entered data
  const pickupsValue = monthlyPickups || prevPickups;
  const floorsValue = (addlFloors || addlFloors === 0) ? addlFloors : prevFloors;
  const buildingsValue = (addlBuildings || addlBuildings === 0) ? addlBuildings : prevBuildings;

  // get the selected pricing plan. if selected plan is cleared, default to MTO
  const pricingPlan = (defaultValues && defaultValues.plan)
    ? defaultValues.plan : PLAN_TYPES.MADE_TO_ORDER;
  // we can also provide specific pricing info via the givenPricing prop
  const pricingData = givenPricing || PLANS[pricingPlan].pricing;
  const { basePickupPrice, isSubscription } = pricingData;
  const monthlyTooltip = isSubscription
    ? 'If you use more than your allotted pickups in a given month, you will be billed extra the following month.'
    : '';

  const {
    totalPrice, floorsPrice, buildingsPrice, pricePerPickup,
  } = useCustomPricing(pricingData, pickupsValue, floorsValue, buildingsValue);

  // const {
  //   totalPrice, floorsPrice, buildingsPrice, pricePerPickup,
  // } = useCustomPricing(pricingData, monthlyPickups, addlFloors, addlBuildings);

  // onMount, if no data is in state, set to defaults and update the form
  useEffect(() => {
    if (!monthlyPickups) {
      setValue('num_monthly_pickups', 4);
      setValue('num_additional_floors', 0);
      setValue('num_additional_buildings', 0);

      setPrevPickups(4);
      setPrevFloors(0);
      setPrevBuildings(0);
    }
  }, []);

  // inputs must be a positive value
  const isValid = () => (
    monthlyPickups > 0 && addlFloors >= 0 && addlBuildings >= 0
  );

  const navigate = useNavigate();
  const onSubmit = () => {
    actions.updateLocationCreateAction(getValues());
    navigate(`../${LOC_CREATE_STAGES.CONFIRM}`);
  };

  var numMonthlyPickupsLabel = '';
  if(defaultValues.plan === PLAN_TYPES.A_LA_CARTE) {
    numMonthlyPickupsLabel = "Estimate Monthly Pickups"
  } else {
    numMonthlyPickupsLabel = "Monthly Pickups"
  }

  return goBack
    ? <Navigate to={`../${LOC_CREATE_STAGES.PLAN_PICKER}`} />
    : (
      <Container className="p-5">
        { !hideButtons && (
        <Row className="mb-5">
          <h2>
            Let's finish up!
          </h2>
        </Row>
        )}
        <Row>
          <Col
            sm={6}
            className="d-flex flex-column justify-content-between"
          >
            <CalculatorInput
              id="num_monthly_pickups"
              label={numMonthlyPickupsLabel}
              labelHelper={`Each pickup is $${basePickupPrice}${isSubscription ? ' / month' : ''}.`}
              tooltip={monthlyTooltip}
              value={monthlyPickups}
              setValue={setValue}
              setPreviousData={setPrevPickups}
            />
            <CalculatorInput
              id="num_additional_floors"
              label="Total Floors"
              labelHelper="How many separate floors do you need a Food Rescuer to collect from?"
              value={addlFloors}
              setValue={setValue}
              setPreviousData={setPrevFloors}
            />
            <CalculatorInput
              id="num_additional_buildings"
              label="Total Buildings"
              labelHelper="How many separate buildings do you need a Food Rescuer to collect from?"
              value={addlBuildings}
              tooltip="Extra buildings must be on the same campus."
              setValue={setValue}
              setPreviousData={setPrevBuildings}
            />
          </Col>
          <Col
            sm={5}
          >
            <div
              className="bg-dark-green rounded-3 text-white minh-card h-100 position-relative p-2"
            >
              <div className="pt-4 px-4">
                <p
                  className="h2 pb-4 mb-4 border-bottom"
                  aria-live="polite"
                >
                  <span>
                    $
                    {totalPrice}
                    {' '}
                  </span>
                  <span className="fw-normal fs-5">
                    / month
                  </span>
                </p>
                <div className="fs-7 fw-light">
                  <p className="mb-1">
                    <span>Base Pickup</span>
                    <span className="float-end">
                      $
                      {basePickupPrice}
                      {' '}
                      / pickup
                    </span>
                  </p>
                  <p className="mb-1">
                    <span>Extra Floors</span>
                    <span className="float-end">
                      +$
                      {floorsPrice}
                      {' '}
                      / pickup
                    </span>
                  </p>
                  <p className="mb-1">
                    <span>Extra Buildings</span>
                    <span className="float-end">
                      +$
                      {buildingsPrice}
                      {' '}
                      / pickup
                    </span>
                  </p>
                  <p className="mt-2 mb-1 fw-normal">
                    <span>
                      {`${pickupsValue} pickup${pickupsValue > 1 ? 's' : ''} / month`}
                    </span>
                    <span className="float-end">
                      {`$${pricePerPickup} / pickup`}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </Col>
        </Row>
        { !hideButtons && (
          <Row className="mt-5">
            <Col className="d-flex justify-content-end">
              <Button
                variant="outline-dark-green"
                onClick={() => setGoBack(true)}
              >
                Back
              </Button>
              <Button
                variant="dark-green"
                disabled={!isValid()}
                className="ms-2"
                onClick={onSubmit}
                type="submit"
              >
                Continue
              </Button>
            </Col>
          </Row>
        )}
      </Container>
    );
}

LocationPricingCalculator.propTypes = {
  givenPricing: CustomPricingPropType,
  hideButtons: PropTypes.bool,
};

LocationPricingCalculator.defaultProps = {
  givenPricing: null,
  hideButtons: false,
};

export default LocationPricingCalculator;

/**
 * Specially styled input
 */
const CalculatorInput = ({
  id, label, labelHelper, tooltip, value, setValue, setPreviousData,
}) => {
  /**
   * parses int value from event and calls setter with it
   * @param {event} e
   * @param {function} setter
   */
  const handleInput = (e) => {
    e.preventDefault();

    const { value: val } = e.target;
    let parsedInt = Number.parseInt(val, 10);

    // only send ADDITIONAL floors and buildings to the server
    if (id === 'num_additional_floors' || id === 'num_additional_buildings') {
      parsedInt -= 1;
    }

    setValue(id, parsedInt);

    if (parsedInt || parsedInt === 0) {
      setPreviousData(parsedInt);
    }
  };

  const handleBlur = (e) => {
    e.preventDefault();
    const { value: val } = e.target;
    const parsedInt = Number.parseInt(val, 10);

    if (!parsedInt) {
      let minVal = 1;
      // only send ADDITIONAL floors and buildings to the server
      if (id === 'num_additional_floors' || id === 'num_additional_buildings') {
        minVal = 0;
      }

      setValue(id, minVal);

      setPreviousData(minVal);
    }
  };

  let displayValue = value;
  if (id === 'num_additional_floors' || id === 'num_additional_buildings') {
    displayValue += 1;
  }

  return (
    <Form.Group className="calculator-input">
      <Form.Label htmlFor={id} className="calculator-input-label">
        <span>{label}</span>
        <br />
        <span className="fw-light fs-7">
          {labelHelper}
          { tooltip && (
            <OverlayTrigger
              placement="left"
              overlay={(
                <Tooltip>
                  {tooltip}
                </Tooltip>
              )}
            >
              <Button
                variant="outline-dark-green"
                className="info-button ms-2"
              >
                i
              </Button>
            </OverlayTrigger>
          )}
        </span>
      </Form.Label>
      <Form.Control
        type="number"
        id={id}
        className="calculator-input-control"
        min={1}
        value={displayValue || ''}
        onChange={(e) => handleInput(e)}
        onBlur={(e) => handleBlur(e)}
      />
    </Form.Group>
  );
};

CalculatorInput.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  labelHelper: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setValue: PropTypes.func.isRequired,
  setPreviousData: PropTypes.func.isRequired,
};

CalculatorInput.defaultProps = {
  tooltip: '',
  value: 1,
};
